if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      quantityRulesController = undefined;
      pendingRequestUrl = null;
      availabilityDraft = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.handleReviewRatingClick = this.handleReviewRatingClick.bind(this);
        this.handleReviewRatingKeydown = this.handleReviewRatingKeydown.bind(this);
        this.handleFitmentSelectorClick = this.handleFitmentSelectorClick.bind(this);
        this.handlePartNumberCopy = this.handlePartNumberCopy.bind(this);
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.initializeProductReviewRatings();
        this.initializeProductFitmentControls();
        this.addEventListener('click', this.handleFitmentSelectorClick);
        this.addEventListener('click', this.handlePartNumberCopy);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.cancelQuantityRulesRefresh();
        this.variantSelectors?.rejectPendingSelectPromise?.(new DOMException('Product was replaced', 'AbortError'));
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.removeEventListener('click', this.handleReviewRatingClick, true);
        this.removeEventListener('keydown', this.handleReviewRatingKeydown);
        this.removeEventListener('click', this.handleFitmentSelectorClick);
        this.removeEventListener('click', this.handlePartNumberCopy);
        this.partNumberCopyRequest = null;
        this.fitmentDialogCleanup?.(false);
        this.fitmentControlsCleanup?.();
      }

      async handlePartNumberCopy(event) {
        const button = event.target.closest('[data-copy-part-number]');
        if (!button || !this.contains(button) || button.disabled || this.partNumberCopyRequest) return;
        const container = button.closest('.product__part-number');
        const status = container?.querySelector('[role="status"]');
        const number = button.dataset.copyPartNumber;
        if (!number || !status) return;
        const request = {};
        this.partNumberCopyRequest = request;
        status.textContent = '';
        let copied = false;
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(number);
            copied = true;
          } else {
            // Older browsers still copy within the original click gesture.
            const input = document.createElement('textarea');
            const focused = document.activeElement;
            input.value = number;
            input.readOnly = true;
            input.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
            this.append(input);
            try {
              input.select();
              copied = document.execCommand('copy');
            } finally {
              input.remove();
              focused?.focus({ preventScroll: true });
            }
          }
        } catch {
          // Permission failures must never produce a successful-copy message.
        }
        if (this.partNumberCopyRequest !== request) return;
        this.partNumberCopyRequest = null;
        if (button.isConnected && this.contains(button)) {
          status.textContent = copied ? container.dataset.copySuccess : container.dataset.copyError;
        }
      }

      initializeProductFitmentControls() {
        const help = this.querySelector('[data-fitment-help]');
        if (!help || !window.ProductFitmentState || !window.ProductFitmentYear) return;
        // Product swaps briefly mount both sections; the global vehicle dialog has one owner.
        ProductInfo.fitmentControlsOwner?.fitmentControlsCleanup?.();
        ProductInfo.fitmentControlsOwner = this;
        const controls = new Map();
        const getContext = () => `${this.dataset.productId}:${this.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value || ''}`;
        const sync = () => {
          const inline = this.querySelector('#cm-verify-fitment');
          const dialog = document.getElementById('cm_VehicleWidgetDialog');
          for (const [host, control] of controls) {
            if (host !== inline && host !== dialog) {
              control.state.destroy();
              control.year.destroy();
              control.observer?.disconnect();
              control.feedback.remove();
              controls.delete(host);
            }
          }
          for (const host of [inline, dialog].filter(Boolean)) {
            if (!controls.has(host)) {
              const feedback = document.createElement('div');
              feedback.className = 'product__fitment-feedback';
              const status = document.createElement('p');
              status.setAttribute('role', 'status');
              status.setAttribute('aria-live', 'polite');
              status.setAttribute('aria-atomic', 'true');
              const recovery = document.createElement('button');
              recovery.type = 'button';
              recovery.className = 'button button--secondary';
              recovery.textContent = help.dataset.reloadLabel;
              recovery.hidden = true;
              recovery.addEventListener('click', () => window.location.reload());
              feedback.append(status, recovery);
              const state = new window.ProductFitmentState(host, {
                status, recovery, getContext,
                messages: {
                  pending: help.dataset.updatingMessage,
                  ready: help.dataset.readyMessage,
                  next: help.dataset.nextMessage,
                  error: help.dataset.failedMessage,
                  unconfirmed: help.dataset.failedMessage,
                },
              });
              const year = new window.ProductFitmentYear(host, {
                label: help.dataset.yearLabel,
                placeholder: help.dataset.yearPlaceholder,
                noMatches: help.dataset.yearNoMatches,
              });
              const observer = host === dialog ? new MutationObserver(sync) : null;
              observer?.observe(host, { childList: true, subtree: true });
              controls.set(host, { state, year, observer, feedback });
            }
            const { feedback } = controls.get(host);
            if (host === inline) {
              if (host.nextElementSibling !== feedback) host.after(feedback);
            } else {
              const container = host.querySelector('.dialog-content') || host.querySelector('.dialog-container');
              if (container && feedback.parentElement !== container) container.append(feedback);
            }
          }
          if (dialog) mountObserver.disconnect();
        };
        const observer = new MutationObserver(sync);
        const mountObserver = new MutationObserver(sync);
        observer.observe(this, { childList: true, subtree: true });
        mountObserver.observe(document.body, { childList: true });
        sync();
        this.fitmentControlsCleanup = () => {
          this.fitmentControlsCleanup = null;
          if (ProductInfo.fitmentControlsOwner === this) ProductInfo.fitmentControlsOwner = null;
          observer.disconnect();
          mountObserver.disconnect();
          controls.forEach(({ state, year, observer: hostObserver, feedback }) => {
            state.destroy();
            year.destroy();
            hostObserver?.disconnect();
            feedback.remove();
          });
          controls.clear();
        };
      }

      handleFitmentSelectorClick(event) {
        const button = event.target.closest?.('[data-fitment-selector]');
        if (button?.closest('product-info') !== this) return;
        const help = button.closest('[data-fitment-help]');
        const status = help?.querySelector('[data-fitment-status]');
        if (!status) return;
        if (typeof window.Convermax?.openVehicleDialog !== 'function') {
          status.textContent = help.dataset.loadingMessage;
          return;
        }
        try {
          status.textContent = '';
          button.focus({ preventScroll: true });
          window.Convermax.openVehicleDialog();
          this.watchFitmentDialog(button, help, status);
        } catch {
          this.fitmentDialogCleanup?.(false);
          status.textContent = help.dataset.unavailableMessage;
        }
      }

      watchFitmentDialog(opener, help, status) {
        ProductInfo.fitmentDialogOwner?.fitmentDialogCleanup?.(false);
        ProductInfo.fitmentDialogOwner = this;
        let host;
        let dialog;
        let mountTimer;
        const visible = (element) => {
          if (!element?.isConnected) return false;
          for (let node = element; node instanceof HTMLElement; node = node.parentElement) {
            const style = getComputedStyle(node);
            if (node.hidden || node.inert || node.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden') return false;
          }
          return true;
        };
        const focusable = () => [...dialog.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]')]
          .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled') && visible(element));
        const focusFirst = () => {
          const fields = focusable();
          (fields.find((element) => element.matches('select, input, textarea')) || fields[0] || dialog).focus({ preventScroll: true });
        };
        const cleanup = (restoreFocus) => {
          observer.disconnect();
          clearTimeout(mountTimer);
          document.removeEventListener('keydown', onKeydown, true);
          if (this.fitmentDialogCleanup !== cleanup) return;
          this.fitmentDialogCleanup = null;
          if (ProductInfo.fitmentDialogOwner === this) ProductInfo.fitmentDialogOwner = null;
          const active = document.activeElement;
          if (restoreFocus && (!active || active === document.body || !active.isConnected || dialog?.contains(active))) {
            if (visible(opener)) opener.focus({ preventScroll: true });
            else {
              const result = this.querySelector('#cm-verify-fitment');
              if (visible(result)) {
                result.setAttribute('tabindex', '-1');
                result.focus({ preventScroll: true });
              }
            }
          }
        };
        const onKeydown = (event) => {
          if (!dialog?.contains(event.target) || !visible(dialog) || event.isComposing) return;
          if (event.key === 'Escape' && event.target.closest('[data-fitment-year]')?.querySelector('[aria-expanded="true"]')) return;
          const close = dialog.querySelector('.close');
          if (event.key === 'Escape' || (close?.contains(event.target) && !close.matches('button, a[href]') && ['Enter', ' '].includes(event.key))) {
            if (!close) return;
            event.preventDefault();
            event.stopPropagation();
            if (!event.repeat) close.click();
          } else if (event.key === 'Tab') {
            const fields = focusable();
            const first = fields[0] || dialog;
            const last = fields[fields.length - 1] || dialog;
            if (!fields.length || event.target === dialog || (event.shiftKey && event.target === first) || (!event.shiftKey && event.target === last)) {
              event.preventDefault();
              (event.shiftKey ? last : first).focus();
            }
          }
        };
        const sync = () => {
          const nextHost = document.getElementById('cm_VehicleWidgetDialog');
          if (nextHost && nextHost !== host) {
            host = nextHost;
            observer.disconnect();
            observer.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
          }
          const next = host?.querySelector('.dialog-container');
          if (!visible(next)) {
            if (dialog) cleanup(true);
            return;
          }
          const changed = next !== dialog;
          const active = document.activeElement;
          const shouldFocus = active === opener || active === document.body || !active?.isConnected || dialog?.contains(active);
          dialog = next;
          clearTimeout(mountTimer);
          const setAttribute = (element, name, value) => {
            if (element.getAttribute(name) !== value) element.setAttribute(name, value);
          };
          setAttribute(dialog, 'role', 'dialog');
          setAttribute(dialog, 'aria-modal', 'true');
          setAttribute(dialog, 'aria-label', opener.textContent.trim());
          setAttribute(dialog, 'tabindex', '-1');
          const close = dialog.querySelector('.close');
          if (close && !close.matches('button, a[href]')) {
            setAttribute(close, 'role', 'button');
            setAttribute(close, 'tabindex', '0');
            setAttribute(close, 'aria-label', help.dataset.closeLabel);
          }
          if (changed && shouldFocus) focusFirst();
        };
        const observer = new MutationObserver(sync);
        this.fitmentDialogCleanup = cleanup;
        document.addEventListener('keydown', onKeydown, true);
        // The provider normally keeps an empty host. Only watch the body until that host mounts.
        observer.observe(document.body, { childList: true });
        mountTimer = setTimeout(() => {
          cleanup(false);
          if (this.isConnected && status.isConnected) status.textContent = help.dataset.unavailableMessage;
        }, 5000);
        sync();
      }

      initializeProductReviewRatings() {
        this.querySelectorAll('.loox-rating').forEach((rating) => {
          const trigger = rating.parentElement;
          if (!trigger?.matches('a')) return;
          if (trigger.hasAttribute('href') && !['#looxReviewsFrame', '#looxReviews'].includes(trigger.getAttribute('href'))) return;
          if (rating.dataset.floatingProductReviewsEnabled !== 'true') {
            const reviews = document.getElementById('looxReviews');
            if (!reviews || !rating.dataset.id || reviews.dataset.productId !== rating.dataset.id) return;
            trigger.setAttribute('href', '#looxReviews');
            trigger.setAttribute('aria-label', window.accessibilityStrings?.productReviews || 'Read product reviews');
            trigger.setAttribute('data-fr-review-link', '');
            if (!trigger.querySelector('.product__review-link-label')) {
              const label = document.createElement('span');
              label.className = 'product__review-link-label';
              label.textContent = trigger.getAttribute('aria-label');
              trigger.append(label);
            }
            reviews.setAttribute('tabindex', '-1');
            return;
          }
          trigger.removeAttribute('href');
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('tabindex', '0');
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-label', window.accessibilityStrings?.productReviews || 'Read product reviews');
          trigger.setAttribute('data-fr-review-trigger', '');
        });
        this.addEventListener('click', this.handleReviewRatingClick, true);
        this.addEventListener('keydown', this.handleReviewRatingKeydown);
      }

      handleReviewRatingClick(event) {
        const link = event.target.closest?.('[data-fr-review-link]');
        if (link?.closest('product-info') === this && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          document.getElementById('looxReviews')?.focus({ preventScroll: true });
          return;
        }
        const trigger = event.target.closest?.('[data-fr-review-trigger]');
        if (trigger?.closest('product-info') !== this) return;
        trigger.focus({ preventScroll: true });
      }

      handleReviewRatingKeydown(event) {
        const trigger = event.target.closest?.('[data-fr-review-trigger]');
        if (trigger?.closest('product-info') !== this || event.target !== trigger) return;
        if (event.isComposing || !['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        if (!event.repeat) trigger.querySelector('.loox-rating[data-floating-product-reviews-enabled="true"]')?.click();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          this.protectAvailabilityForms(newNode);
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        this.resetProductFormState(!shouldSwapProduct);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      clearVariantSpecifications(preserveShared = true) {
        const specifications = this.querySelector(`#ProductSpecifications-${this.dataset.section}`);
        if (!specifications) return;

        specifications.querySelector('product-specifications')?.remove();
        const sharedRows = specifications.querySelector('[data-product-specifications-shared] [data-specification]');
        if (preserveShared && sharedRows) {
          specifications.classList.remove('hidden');
        } else {
          specifications.replaceChildren();
          specifications.classList.add('hidden');
        }
      }

      resetProductFormState(preserveSharedSpecifications = true) {
        this.partNumberCopyRequest = null;
        this.cancelQuantityRulesRefresh();
        this.clearVariantSpecifications(preserveSharedSpecifications);
        this.querySelectorAll('[data-copy-part-number]').forEach((button) => { button.disabled = true; });
        const copyStatus = this.querySelector('.product__part-copy-status');
        if (copyStatus) copyStatus.textContent = '';
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
        this.querySelector('.shopify-payment-button')?.setAttribute('inert', '');
        this.captureAvailabilityDraft();
        this.querySelector(`#AvailabilityHelp-${this.dataset.section}`)?.setAttribute('inert', '');
      }

      captureAvailabilityDraft() {
        const form = this.querySelector('.product-availability__form');
        if (!form) return;

        const fieldNames = ['contact[email]', 'contact[Vehicle]', 'contact[body]'];
        if (!form.elements.namedItem(fieldNames[0])) {
          this.availabilityDraft = null;
          return;
        }

        this.availabilityDraft = {
          fields: Object.fromEntries(fieldNames.map((name) => [name, form.elements.namedItem(name)?.value || ''])),
          open: form.querySelector('details')?.open ?? false,
        };
      }

      updateAvailabilityHelp(html) {
        const source = html.getElementById(`AvailabilityHelp-${this.sectionId}`);
        const destination = this.querySelector(`#AvailabilityHelp-${this.dataset.section}`);
        if (!source || !destination) return;

        this.captureAvailabilityDraft();
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('hidden');
        destination.removeAttribute('inert');

        const form = destination.querySelector('.product-availability__form');
        if (form && this.availabilityDraft) {
          // Keep only customer-entered fields; variant and product context always come from Shopify.
          Object.entries(this.availabilityDraft.fields).forEach(([name, value]) => {
            const field = form.elements.namedItem(name);
            if (field) field.value = value;
          });
          const details = form.querySelector('details');
          if (details && this.availabilityDraft.open) details.open = true;
        }
        this.protectAvailabilityForms(destination);
      }

      protectAvailabilityForms(container) {
        // Shopify contact forms inserted after DOMContentLoaded need CAPTCHA wiring.
        container.querySelectorAll('.product-availability__form').forEach((form) => {
          window.Shopify?.captcha?.protect?.(form);
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise?.(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      async renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        try {
          const response = await fetch(requestUrl, { signal });
          if (!response.ok) throw new Error(`Product selection request failed: ${response.status}`);
          const responseText = await response.text();
          if (signal.aborted) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          if (!html.querySelector('product-info')) throw new Error('Product selection response is incomplete');

          this.pendingRequestUrl = null;
          callback(html);
          const submitButton = this.productForm?.submitButton;
          this.querySelector('.shopify-payment-button')?.toggleAttribute(
            'inert',
            submitButton ? (window.RegionalRestriction?.getControlState(submitButton).disabled ?? submitButton.disabled) : true
          );
          // Set focus to the last clicked option value.
          document.getElementById(targetId)?.focus();
        } catch (error) {
          if (signal.aborted || error.name === 'AbortError') return;
          console.error(error);
          this.variantSelectors?.rejectPendingSelectPromise?.(error);
          this.showProductSelectionError(requestUrl);
        }
      }

      showProductSelectionError(requestUrl) {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        this.querySelector('.shopify-payment-button')?.setAttribute('inert', '');
        this.querySelector(`#AvailabilityHelp-${this.dataset.section}`)?.setAttribute('inert', '');
        // A full page load retries the selected options without submitting the stale variant.
        const retryUrl = new URL(requestUrl, window.location.origin);
        retryUrl.searchParams.delete('section_id');
        const message = "We couldn't load this selection. Reload it to check availability before adding to your cart. ";
        const retryLabel = 'Reload this selection';
        this.querySelector('ws-configurator')?.showVariantSelectionError?.(message, retryUrl.href, retryLabel);
        productForm?.handleErrorMessage(message);
        if (!productForm?.errorMessage || productForm.hideErrors) return;

        const retryLink = document.createElement('a');
        retryLink.href = retryUrl.href;
        retryLink.className = 'link underlined-link';
        retryLink.textContent = retryLabel;
        productForm.errorMessage.append(retryLink);
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise?.(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          // Helper function to update DOM from response HTML
          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          // Build variant title from the scoped variant picker so we only use real option controls.
          const selectedOptions = Array.from(
            this.variantSelectors?.querySelectorAll('select option[selected], fieldset input:checked') || []
          )
            .map(({ value }) => value)
            .filter(Boolean);

          if (selectedOptions.length > 0) {
            const variantTitleElement = this.querySelector(`#VariantTitle-${this.dataset.section}`);
            if (variantTitleElement) {
              variantTitleElement.textContent = '\u00a0' + selectedOptions.join(' / ');
            }
          }

          // Update part number from server response (only if variant is valid)
          if (variant) {
            updateSourceFromDestination('PartNumber');
            updateSourceFromDestination('ProductSpecifications', ({ classList }) => classList.contains('hidden'));
          } else {
            this.querySelector(`#PartNumber-${this.dataset.section}`)?.replaceChildren();
            this.clearVariantSpecifications();
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          updateSourceFromDestination('price');
          updateSourceFromDestination('ShippingEstimate');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          this.updateAvailabilityHelp(html);
          updateSourceFromDestination('PurchasePolicies');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        this.querySelector('ws-configurator')?.showVariantSelectionError?.(window.variantStrings.unavailable);
        this.querySelector(`#AvailabilityHelp-${this.dataset.section}`)?.setAttribute('inert', '');

        const selectors = ['price', 'ShippingEstimate', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules', 'AvailabilityHelp', 'PurchasePolicies']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max !== null) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      cancelQuantityRulesRefresh() {
        this.quantityRulesController?.abort();
        this.quantityRulesController = undefined;
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden');
      }

      fetchQuantityRules() {
        this.cancelQuantityRulesRefresh();
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId || this.pendingRequestUrl) return;

        const controller = new AbortController();
        this.quantityRulesController = controller;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`, { signal: controller.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (controller.signal.aborted || this.quantityRulesController !== controller || !this.isConnected ||
              this.productForm?.variantIdInput?.value !== currentVariantId) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => { if (!controller.signal.aborted) console.error(e); })
          .finally(() => {
            if (this.quantityRulesController !== controller) return;
            this.quantityRulesController = undefined;
            spinner?.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
